import type { Action, Dispatch } from "@reduxjs/toolkit";
import { Table, type FormInstance } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { HookAPI } from "antd/es/modal/useModal";
import type { ColumnType, TableProps } from "antd/es/table";
import type { SortOrder, TablePaginationConfig } from "antd/es/table/interface";
import { useEffect, useMemo, useState, type Key } from "react";
import { connect, useDispatch } from "react-redux";
import type { State } from "router5";
import type { IntlShape } from "@formatjs/intl";
import type { RootState } from "../../app/store";

export type PaginationAndFilter = {
	total_items: number;
	total_pages: number;
	current_page: number;
	per_page: number;
};

function maxPagination(total: number, perPage: number): number {
	const maxPages =
		Math.floor(total / perPage) + (total % perPage === 0 ? 0 : 1);
	return maxPages;
}

export type TableWrapperProps<DataType, RowType> = TableProps & {
	data: DataType;
	getRows: (
		intl: IntlShape,
		dispatch: Dispatch,
		data: DataType,
		modalApi?: HookAPI,
	) => RowType[];
	intl: IntlShape;
	getColumns: (intl: IntlShape, currentFilter?: AnyObject) => ColumnType[];
	pagination?: PaginationAndFilter;
	route?: State;
	filterForm?: FormInstance;
	modalApi?: HookAPI;
	applyFilterAction?: (filterParams: AnyObject) => Action;
	localSort?: (
		rows: RowType[],
		columnKey?: Key,
		order?: SortOrder,
	) => RowType[] | undefined;
};

export function TableWrapper<DataType, RowType>(
	props: TableWrapperProps<DataType, RowType>,
) {
	const {
		data,
		getRows,
		getColumns,
		pagination,
		route,
		filterForm,
		intl,
		modalApi,
		applyFilterAction,
		localSort,
		...otherTableProps
	} = props;

	const dispatch = useDispatch();

	// const modalApi = store.getState().appFeedback.modal;
	const [sorted, setSorted] = useState([] as RowType[]);
	const currentFilter = route?.params;

	const tableColumns = useMemo(() => {
		const columns = intl ? getColumns(intl, currentFilter) : undefined;
		return columns;
	}, [getColumns, intl, currentFilter]);
	useEffect(() => {
		if (getRows && intl && data) {
			setSorted((_previous) => getRows(intl, dispatch, data, modalApi));
		} else {
			setSorted((_previous) => []);
		}
	}, [getRows, data, intl, dispatch, modalApi]);

	const tablePagination: TablePaginationConfig | false = useMemo(() => {
		if (pagination && applyFilterAction) {
			filterForm?.setFieldsValue(currentFilter);
			return {
				total: pagination.total_items,
				current: pagination.current_page,
				pageSize: pagination.per_page,
				pageSizeOptions: [10, 25, 50, 100],
				onShowSizeChange: (page, perPage) => {
					const maxPages = maxPagination(pagination.total_items, perPage);

					localStorage.setItem("per_page", perPage + "");
					dispatch(
						applyFilterAction({
							...currentFilter,
							page: Math.min(page, maxPages),
							per_page: perPage,
							sort: currentFilter?.sort,
						}),
					);
				},
				showSizeChanger: true,
				showQuickJumper: false,
				onChange: (page, perPage) => {
					const maxPages = maxPagination(pagination.total_items, perPage);

					localStorage.setItem("per_page", perPage + "");
					dispatch(
						applyFilterAction({
							...currentFilter,
							page: Math.min(page, maxPages),
							per_page: perPage,
							sort: currentFilter?.sort,
						}),
					);
				},
			};
		}
		return false;
	}, [dispatch, filterForm, applyFilterAction, pagination, currentFilter]);

	const onChange: TableProps["onChange"] = (
		pagination,
		filters,
		sorter,
		extra,
	) => {
		if (extra.action == "sort") {
			const firstSorter = Array.isArray(sorter) ? sorter[0] : sorter;
			if (applyFilterAction) {
				const orderDesc = firstSorter.order == "descend" ? "-" : "";
				dispatch(
					applyFilterAction({
						...currentFilter,
						page: pagination.current,
						per_page: pagination.pageSize,
						sort: firstSorter.order
							? orderDesc + firstSorter.columnKey
							: undefined,
					}),
				);
			} else if (localSort && data) {
				setSorted((draft) => {
					const sorted = localSort(
						draft,
						firstSorter.columnKey,
						firstSorter.order,
					);
					return sorted ?? getRows(intl, dispatch, data, modalApi);
				});
			}
		}
	};

	return intl ? (
		<Table
			columns={tableColumns}
			dataSource={sorted as AnyObject[]}
			pagination={tablePagination}
			onChange={onChange}
			scroll={{ x: "max-content" }}
			{...otherTableProps}
		/>
	) : null;
}

export const ConnectedTable = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
}))(TableWrapper);

export default ConnectedTable;
